import { Col, Row, Card, CardBody, Form, Input, CardFooter, Button } from "reactstrap";
import { queryKeyes } from 'shared/queryKeys';
import { FormikProps, useFormik } from 'formik';
import ErrorComponent from 'Components/ErrorComponent';
import apiGlobal from 'global/api.global';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import * as Yup from "yup";
import { handleServerResponse, isConfigurationButtonDisabled } from 'GenericForms/Helper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'index';
import ToolTip from "Components/ToolTip";
import { VesselConfigrationTabs } from "shared/constants";
import React, { useEffect } from "react";
import { queryClient } from "react-query/queryClient";
import isEqual from "fast-deep-equal";
import Message from "Components/Message";
import FormLoading from "Components/FormLoading";
import { getConfigurationLimitsAction } from "Store/Generic/ReportingSlice";

interface GeneralSettingsType {
    refreshVesselMachineries: number;
    setRefreshVesselMachineries: (value: number) => void;
    setCheckValuesBeforeSwitch: (value: boolean) => void;
    setTabName: (value: string) => void;
    setFormik?: (value: FormikProps<any>) => void;
    formik?: FormikProps<any>;
    VesselConfActiveTab: number;
}

const GeneralSettings = ({
    refreshVesselMachineries,
    setRefreshVesselMachineries,
    setCheckValuesBeforeSwitch,
    setTabName,
    setFormik,
    VesselConfActiveTab,
}: GeneralSettingsType) => {
    /** state variables start */
    const { VesselID, Vessels, ConfigurationLimits, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const dispatch = useDispatch();
    /** state variables end */

    /** Machinery on vessel */
    const Machinary = ConfigurationLimits?.vessel_machinery_list || [];

    /** Assign initial values to formik object */
    const getInitailValueGeneralSettings = () => {
        if (Machinary?.length > 0 && Machinary[0].id > 0) {
            return Machinary
        } else {
            return []
        }
    }

    /** General Settings Formik Object */
    const GeneralSettingsFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            generalSettings: getInitailValueGeneralSettings()
        },
        validationSchema: Yup.object().shape({
            generalSettings: Yup.array(Yup.object({
                running_hour: Yup.boolean(),
                running_counter: Yup.boolean(),
                energy_parameter: Yup.boolean(),
            }))
        }),
        onSubmit: async () => {
            let responseArray: any[] = [];
            if (Machinary?.length > 0 && Machinary[0].id > 0) {
                GeneralSettingsFormik?.values?.generalSettings?.forEach((machine: any) => {
                    responseArray.push(apiGlobal.put(`/vessel_machinery_list/${machine.id}/`, machine));
                })
                /** handle server response */
                await handleServerResponse(responseArray).then(async (res) => {
                    if (res) {
                        dispatch(getConfigurationLimitsAction(VesselID as number));
                        await queryClient.invalidateQueries(queryKeyes.vessel.EconFMCounterObject.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.VesselMachineries.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.vesselRunningHoursMachinery.key);
                        setRefreshVesselMachineries(refreshVesselMachineries + 1);
                    }
                });
            }
            GeneralSettingsFormik.setSubmitting(false);
        }
    })

    const tabName = 'General Settings';
    const handleFormikStateChange = (values: any, initialValues: any) => {
        const areValuesEqual = isEqual(values, initialValues);
        setCheckValuesBeforeSwitch(areValuesEqual);
        setTabName(tabName);
        return areValuesEqual;
    };

    /**Start of useEffect */
    useEffect(() => {
        if (VesselConfActiveTab === VesselConfigrationTabs.GENERAL_SETTINGS && GeneralSettingsFormik.values !== GeneralSettingsFormik.initialValues) {
            setFormik(GeneralSettingsFormik);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselConfActiveTab, GeneralSettingsFormik?.values]);
    useEffect(() => {
        handleFormikStateChange(GeneralSettingsFormik?.values, GeneralSettingsFormik?.initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GeneralSettingsFormik?.values, GeneralSettingsFormik?.initialValues]);
    /**End of useEffect */

    if (reportingLoading) {
        return <FormLoading />
    } else if (reportingError) {
        return <ErrorComponent message='Unable to load required data!' />
    } else {
        return (
            <Card className='border-0'>
                {!(reportingLoading) && !(reportingError) &&
                    <Form onSubmit={GeneralSettingsFormik?.handleSubmit} noValidate autoComplete='off'>
                        <CardBody className='p-0'>
                            <Row>
                                <h4 className="mb-3 navbar_menu">General Settings</h4>
                                <Col sm={12}>
                                    <div className="table-responsive p-0">
                                        <table className="table mb-2" key={refreshVesselMachineries}>
                                            <thead className="table-light">
                                                <tr>
                                                    <th className='p-2 text-center align-middle sr-no-width'>#</th>
                                                    <th className='p-2 align-middle'>Machinery name</th>
                                                    <th className='p-2 align-middle'>Machinery type</th>
                                                    <th className='p-2 text-center align-middle' id="running_hour_msg">Running hours <i className='bx bx-info-circle ml-2p' /></th>
                                                    <th className='p-2 text-center align-middle' id="running_counter_msg">Running counter <i className='bx bx-info-circle ml-2p' /></th>
                                                    <th className='p-2 text-center align-middle' id="energy_parameter_msg">Energy parameter <i className='bx bx-info-circle ml-2p' /></th>
                                                    <ToolTip target="energy_parameter_msg" message="Check the box if machinery is fitted with energy meter / Kilowatt-hour meter" />
                                                    <ToolTip target="running_hour_msg" message="Check the box if running hours need to be reported in the daily reporting" />
                                                    <ToolTip target="running_counter_msg" message="Check the fields if running counters need to be reported in daily reporting." />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <React.Fragment>
                                                    {GeneralSettingsFormik?.values?.generalSettings && GeneralSettingsFormik?.values?.generalSettings.length > 0 ? (
                                                        GeneralSettingsFormik?.values?.generalSettings.map((generalSettings: any, index: number) => {
                                                            return (
                                                                <tr key={generalSettings.id}>
                                                                    <td className='p-2 align-middle text-center'>{index + 1}</td>
                                                                    <td className='p-2 align-middle'>{generalSettings.vessel_machinery_name}</td>
                                                                    <td className='p-2 align-middle'>{generalSettings.machinery_name}</td>
                                                                    <td className='p-2 align-middle text-center'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type='checkbox'
                                                                            id={`general-setting-${index}`}
                                                                            name={`generalSettings.${index}.running_hour`}
                                                                            checked={GeneralSettingsFormik?.values?.generalSettings[index]?.running_hour}
                                                                            onChange={(e: any) => {
                                                                                GeneralSettingsFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={GeneralSettingsFormik?.values?.generalSettings[index]?.running_hour}
                                                                            disabled={isConfigurationButtonDisabled(vessel)}
                                                                        />
                                                                    </td>
                                                                    <td className='p-2 align-middle text-center'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type='checkbox'
                                                                            id={`general-setting-${index}`}
                                                                            name={`generalSettings.${index}.running_counter`}
                                                                            checked={GeneralSettingsFormik.values?.generalSettings[index]?.running_counter}
                                                                            onChange={(e: any) => {
                                                                                GeneralSettingsFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={GeneralSettingsFormik.values?.generalSettings[index]?.running_counter}
                                                                            disabled={isConfigurationButtonDisabled(vessel) || !GeneralSettingsFormik.values?.generalSettings[index]?.running_hour}
                                                                        />
                                                                    </td>
                                                                    <td className='p-2 align-middle text-center'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type='checkbox'
                                                                            id={`general-setting-${index}`}
                                                                            name={`generalSettings.${index}.energy_parameter`}
                                                                            checked={GeneralSettingsFormik.values?.generalSettings[index]?.energy_parameter}
                                                                            onChange={(e: any) => {
                                                                                GeneralSettingsFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={GeneralSettingsFormik.values?.generalSettings[index]?.energy_parameter}
                                                                            disabled={isConfigurationButtonDisabled(vessel)}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={6} className="text-center">
                                                                <Message
                                                                    message="No General Settings data available. Please add general settings details."
                                                                    type="warning"
                                                                    width="40rem" />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter className='p-2'>
                            <Button type="submit" color='primary' className='justify_right' disabled={isConfigurationButtonDisabled(vessel) || GeneralSettingsFormik.isSubmitting}>
                                {GeneralSettingsFormik.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                Save</Button>
                        </CardFooter>
                        <Row className='mt-2'>
                            <FormValuesDebug values={[GeneralSettingsFormik?.values, GeneralSettingsFormik?.errors, GeneralSettingsFormik.initialValues]} />
                        </Row>
                    </Form>
                }
            </Card >
        )
    };
};
export default GeneralSettings;
